/**
 * agencyContextStore.ts
 *
 * Module-level singleton holding the current Ecolane agency/tenant scope, as
 * returned by the BFF in the /session response (`agency_id`, `tenant_id`).
 *
 * Conv-AI reads it to build the `context` block on every /query call, so the
 * backend can check the scope the browser believes it is in against the scope
 * the BFF actually bound the session to.
 *
 * IT IS NOT A TRUST BOUNDARY. Conv-AI does not take the agency from here — it
 * takes it from its own BFF introspection and REFUSES the request (403) when
 * the two disagree. So this is a statement of intent that makes a mismatch
 * detectable, never the thing that decides which agency's data is returned. A
 * tampered value cannot widen scope; it can only get the request rejected.
 *
 * Design mirrors odsAgentStore / geoTokenStore: written by AuthProvider on
 * session bootstrap, cleared on logout, read synchronously from outside
 * React's render cycle. In memory only — never persisted.
 */

export interface AgencyContext {
  /** The Ecolane agency this session is bound to, e.g. "dev-react". */
  agencyId: string | null;
  /** The tenant, usually equal to agencyId but carried separately. */
  tenantId: string | null;
}

let _agencyId: string | null = null;
let _tenantId: string | null = null;

const clean = (value: string | null | undefined): string | null =>
  value?.trim() ? value.trim() : null;

/** Write the scope after a successful /session bootstrap. */
export const setAgencyContext = (
  agencyId: string | null | undefined,
  tenantId: string | null | undefined,
): void => {
  _agencyId = clean(agencyId);
  _tenantId = clean(tenantId);
};

/**
 * Read the scope. Either field may be null when the session has not bootstrapped
 * yet or the BFF did not return it — callers must omit what they do not have
 * rather than substituting a default. There is no default agency.
 */
export const getAgencyContext = (): AgencyContext => ({
  agencyId: _agencyId,
  tenantId: _tenantId,
});

/** Clear on logout / session loss. */
export const clearAgencyContext = (): void => {
  _agencyId = null;
  _tenantId = null;
};
